import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTiers();
  }, []);

  async function fetchTiers() {
    try {
      const response = await axios.get(`${API_URL}/billing/tiers`);
      setTiers(response.data.tiers || []);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <Sparkles className="w-8 h-8 text-brand-600" />
              <span className="ml-2 text-xl font-bold text-surface-900">Virtual Try-On</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-surface-700 hover:text-surface-900">
                Login
              </Link>
              <Link
                to="/signup"
                className="bg-brand-600 text-white px-4 py-2 rounded-xl hover:bg-brand-700"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Pricing Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-surface-600">
            Start with a free trial. Upgrade anytime. Cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-4 gap-8">
          {tiers.map((tier) => (
            <PricingCard key={tier.id} tier={tier} />
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <FAQItem
              question="What happens after my trial ends?"
              answer="Your trial includes 50 generations for 7 days. After that, you'll need to choose a paid plan to continue using the service."
            />
            <FAQItem
              question="Can I change plans later?"
              answer="Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately."
            />
            <FAQItem
              question="What counts as a 'generation'?"
              answer="Each time a unique product/model combination is generated, it counts as one generation. Cached results (shown to other customers) don't count."
            />
            <FAQItem
              question="Do you offer refunds?"
              answer="We offer a 7-day free trial so you can test the service. After that, we provide pro-rated refunds on request."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function PricingCard({ tier }) {
  const features = Array.isArray(tier.features) ? tier.features : [];
  const isPopular = tier.tier_name === 'premium';

  return (
    <div className={`bg-white rounded-xl shadow-card p-8 relative ${
      isPopular ? 'ring-2 ring-brand-600 transform scale-105' : ''
    }`}>
      {isPopular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <span className="bg-brand-600 text-white px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2">{tier.display_name}</h3>
        <div className="text-4xl font-bold mb-2">
          ${tier.price_monthly}
          <span className="text-lg text-surface-600">/mo</span>
        </div>
        <p className="text-surface-600">{tier.description}</p>
      </div>

      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className="w-5 h-5 text-success-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-surface-700">{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/signup"
        className={`block text-center py-3 px-6 rounded-xl font-medium ${
          isPopular
            ? 'bg-brand-600 text-white hover:bg-brand-700'
            : 'bg-brand-100 text-brand-600 hover:bg-brand-200'
        }`}
      >
        {tier.tier_name === 'trial' ? 'Start Free Trial' : 'Get Started'}
      </Link>
    </div>
  );
}

function FAQItem({ question, answer }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-card">
      <h3 className="font-bold text-lg mb-2">{question}</h3>
      <p className="text-surface-600">{answer}</p>
    </div>
  );
}
